import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  TOOL_PREP_PREVIEW_BG,
} from "../../../../utils/editorEntrySession";
import { getTransform } from "../../../../utils/getTransform";
import {
  aspectFromSize,
  thumbSizeFromAspect,
  TOOL_PREP_CUTOUT_REVEAL_MS,
  useDelayedImageReady,
  useImageNaturalSize,
} from "../../lib/toolPrepImageLayout";
import RoundLoader from "../../../../components/ui/RoundLoader";
import ToolPrepUploadDropzone from "../Popup/ToolPrepUploadDropzone";
import {
  ToolPrepOriginalOverlays,
} from "../ToolPrepSourceIdentity";
import ToolPrepRemoveBgBlock from "../Popup/ToolPrepRemoveBgBlock";
import { useLiveSourceCardStats } from "../../hooks/useLiveSourceCardStats";
import "./ToolPrepPreviewBgCards.css";
import "../ToolPrepSourceCards.css";

const FALLBACK_ASPECT = 3 / 4;
const DEFAULT_THUMB_WIDTH = 96;
const GRID_GAP_PX = 4;
const FRAME_CHROME_PX = 4; // 2px border × 2

/**
 * Shared preview-context cards for Shadow / Outline / Background prep.
 *
 * - No image: single upload placeholder (full width)
 * - With image: Original → Transparent → Preview → optional Remove Background
 *   (Replace hover + Remove X on Original)
 */
const ToolPrepPreviewBgCards = ({
  previewBg = TOOL_PREP_PREVIEW_BG.TRANSPARENT,
  onPreviewBgChange = () => {},
  cutoutUrl = null,
  uploadedImageUrl = null,
  cutoutPending = false,
  thumbWidthPx: thumbWidthProp = null,
  showDropShadowOnCutout = false,
  /** Tool-specific fill card (shown last). */
  toolCardColor = null,
  toolCardLabel = null,
  toolCardMode = null,
  /** @deprecated Use toolCard* — kept for call-site compatibility */
  firstCardColor = null,
  firstCardLabel = null,
  firstCardMode = null,
  onUploadClick = null,
  onRemove = null,
  uploadProgress = null,
  sourceName = null,
  sourceSizeLabel = null,
  actionsDisabled = false,
  className = "",
  /**
   * Optional Remove Background controls shown under the compare cards.
   * @type {null | { active?: boolean, onToggle?: Function, disabled?: boolean, locked?: boolean, bio?: string, error?: string|null, label?: string }}
   */
  removeBgControls = null,
}) => {
  const gridRef = useRef(null);
  const [measuredThumbWidth, setMeasuredThumbWidth] = useState(
    DEFAULT_THUMB_WIDTH
  );

  useEffect(() => {
    if (thumbWidthProp != null || !uploadedImageUrl) return undefined;
    const el = gridRef.current;
    if (!el) return undefined;

    const measure = () => {
      const w = el.clientWidth;
      if (w <= 0) return;
      // 3 columns, 2 gaps; thumb sits inside frame chrome
      const col = (w - GRID_GAP_PX * 2) / 3;
      const next = Math.max(48, Math.floor(col - FRAME_CHROME_PX));
      setMeasuredThumbWidth(next);
    };

    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [thumbWidthProp, uploadedImageUrl]);

  const thumbWidthPx =
    thumbWidthProp != null ? thumbWidthProp : measuredThumbWidth;

  const measureUrl = cutoutUrl || uploadedImageUrl;
  const naturalSize = useImageNaturalSize(measureUrl);
  const originalReady = useDelayedImageReady(
    uploadedImageUrl,
    TOOL_PREP_CUTOUT_REVEAL_MS
  );

  const accentMode =
    toolCardMode || firstCardMode || TOOL_PREP_PREVIEW_BG.WHITE;
  const accentLabel = toolCardLabel || firstCardLabel || "Preview";
  const accentThumbBg =
    accentMode === TOOL_PREP_PREVIEW_BG.COLOR
      ? toolCardColor || firstCardColor || "#FFFFFF"
      : "#FFFFFF";

  const aspect = useMemo(
    () =>
      aspectFromSize(
        naturalSize.width,
        naturalSize.height,
        FALLBACK_ASPECT
      ),
    [naturalSize]
  );

  const thumbSize = useMemo(
    () => thumbSizeFromAspect(aspect, thumbWidthPx, FALLBACK_ASPECT),
    [aspect, thumbWidthPx]
  );

  const fitTransform = useMemo(
    () =>
      getTransform(
        naturalSize.width,
        naturalSize.height,
        thumbSize.width,
        thumbSize.height,
        "fit"
      ),
    [naturalSize, thumbSize]
  );

  const renderThumbImage = (src, withShadow = false) => {
    if (!src) return null;
    const hasTransform = fitTransform.width > 0;
    return (
      <img
        src={src}
        alt=""
        className={
          hasTransform
            ? `tpp-bg-thumb__img${withShadow ? " tpp-bg-thumb__img--shadow" : ""}`
            : `tpp-bg-thumb__img tpp-bg-thumb__img--fallback${
                withShadow ? " tpp-bg-thumb__img--shadow" : ""
              }`
        }
        style={
          hasTransform
            ? {
                width: fitTransform.width,
                height: fitTransform.height,
                left: fitTransform.x,
                top: fitTransform.y,
              }
            : undefined
        }
        draggable={false}
      />
    );
  };

  const thumbStyle = {
    width: "100%",
    height: thumbSize.height,
    aspectRatio: `${thumbSize.aspect}`,
  };

  const showCutout = Boolean(cutoutUrl) && !cutoutPending;

  const { originalSizeFootnote, previewSizeFootnote } = useLiveSourceCardStats({
    originalImageUrl: uploadedImageUrl,
    previewImageUrl: cutoutUrl || uploadedImageUrl,
    originalSizeLabel: sourceSizeLabel,
    enabled: Boolean(uploadedImageUrl) && uploadProgress == null,
  });

  const renderCutoutThumb = (variantClass, bgOverride = null) => (
    <div
      className={`tpp-bg-thumb ${variantClass}`}
      style={{
        ...thumbStyle,
        ...(bgOverride ? { backgroundColor: bgOverride } : null),
      }}
    >
      {showCutout ? (
        renderThumbImage(cutoutUrl, showDropShadowOnCutout)
      ) : (
        <div className="tpp-bg-thumb__loader">
          <RoundLoader size="sm" />
        </div>
      )}
    </div>
  );

  // Preparing upload
  if (uploadProgress != null) {
    return (
      <div
        className={`tpp-bg-upload-only tpp-bg-swap${
          className ? ` ${className}` : ""
        }`}
      >
        <ToolPrepUploadDropzone kind="image" progress={uploadProgress} />
      </div>
    );
  }

  // Step 1 — single upload placeholder (no Preview card yet)
  if (!uploadedImageUrl) {
    return (
      <div
        className={`tpp-bg-upload-only tpp-bg-swap${
          className ? ` ${className}` : ""
        }`}
      >
        <ToolPrepUploadDropzone
          kind="image"
          onClick={() => onUploadClick?.()}
        />
      </div>
    );
  }

  // Step 2 — Original | Transparent | Preview → optional Remove Background
  return (
    <div
      className={`tool-prep-source-cards tool-prep-source-cards--filled tpp-bg-filled${
        className ? ` ${className}` : ""
      }`}
    >
      <div ref={gridRef} className="tpp-bg-grid tpp-bg-swap">
        <div
          className={`tpp-bg-card${
            previewBg === TOOL_PREP_PREVIEW_BG.ORIGINAL
              ? " tpp-bg-card--active"
              : ""
          }`}
        >
          <div className="tpp-bg-frame-wrap">
            <button
              type="button"
              className="tpp-bg-card__hit"
              onClick={() => onPreviewBgChange(TOOL_PREP_PREVIEW_BG.ORIGINAL)}
              aria-pressed={previewBg === TOOL_PREP_PREVIEW_BG.ORIGINAL}
              aria-label="Show original"
            >
              <div className="tpp-bg-frame">
                <div
                  className="tpp-bg-thumb tpp-bg-thumb--original"
                  style={thumbStyle}
                >
                  {originalReady ? (
                    renderThumbImage(uploadedImageUrl, false)
                  ) : (
                    <div className="tpp-bg-thumb__loader">
                      <RoundLoader size="sm" />
                    </div>
                  )}
                </div>
              </div>
            </button>
            <ToolPrepOriginalOverlays
              onReplace={onUploadClick}
              onRemove={onRemove}
              disabled={actionsDisabled}
            />
          </div>
          <div className="tool-prep-source-cards__caption">
            <button
              type="button"
              className="tpp-bg-card__label-hit"
              onClick={() => onPreviewBgChange(TOOL_PREP_PREVIEW_BG.ORIGINAL)}
            >
              <span className="tpp-bg-label">Original</span>
            </button>
            {originalSizeFootnote ? (
              <span className="tool-prep-source-cards__card-stat">
                {originalSizeFootnote}
              </span>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          className={`tpp-bg-card${
            previewBg === TOOL_PREP_PREVIEW_BG.TRANSPARENT
              ? " tpp-bg-card--active"
              : ""
          }`}
          onClick={() => onPreviewBgChange(TOOL_PREP_PREVIEW_BG.TRANSPARENT)}
          aria-pressed={previewBg === TOOL_PREP_PREVIEW_BG.TRANSPARENT}
        >
          <div className="tpp-bg-frame">
            {renderCutoutThumb("tpp-bg-thumb--transparent")}
          </div>
          <div className="tool-prep-source-cards__caption">
            <span className="tpp-bg-label">Transparent</span>
            {previewSizeFootnote ? (
              <span className="tool-prep-source-cards__card-stat">
                {previewSizeFootnote}
              </span>
            ) : null}
          </div>
        </button>
        <button
          type="button"
          className={`tpp-bg-card${
            previewBg === accentMode ? " tpp-bg-card--active" : ""
          }`}
          onClick={() => onPreviewBgChange(accentMode)}
          aria-pressed={previewBg === accentMode}
        >
          <div className="tpp-bg-frame">
            {renderCutoutThumb(
              accentMode === TOOL_PREP_PREVIEW_BG.COLOR
                ? "tpp-bg-thumb--color"
                : "tpp-bg-thumb--white",
              accentMode === TOOL_PREP_PREVIEW_BG.COLOR ? accentThumbBg : null
            )}
          </div>
          <div className="tool-prep-source-cards__caption">
            <span className="tpp-bg-label">{accentLabel}</span>
            {previewSizeFootnote ? (
              <span className="tool-prep-source-cards__card-stat">
                {previewSizeFootnote}
              </span>
            ) : null}
          </div>
        </button>
      </div>

      {removeBgControls ? (
        <ToolPrepRemoveBgBlock
          inScroll
          active={Boolean(removeBgControls.active)}
          onToggle={removeBgControls.onToggle}
          disabled={Boolean(removeBgControls.disabled)}
          locked={Boolean(removeBgControls.locked)}
          bio={removeBgControls.bio || ""}
          error={removeBgControls.error || null}
          label={removeBgControls.label || "Remove Background"}
        />
      ) : null}
    </div>
  );
};

export default ToolPrepPreviewBgCards;
